import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, ArrowRight, Clock, Calendar, CheckCircle2,
  ChevronRight, AlertCircle, Target, ListChecks, Milestone,
  GitBranch, Flag,
} from 'lucide-react';
import {
  getImplementationRegistry,
  getPhaseById,
  getWorkstreamById,
  getPhaseStatusColor,
  getPhaseStatusLabel,
} from '../../lib/implementationRegistry';

const TABS = ['Description', 'Objectives', 'Deliverables', 'Tasks', 'Gate Criteria'] as const;
type Tab = typeof TABS[number];

const TAB_ICONS: Record<Tab, React.ReactNode> = {
  Description: <GitBranch className="w-4 h-4" />,
  Objectives: <Target className="w-4 h-4" />,
  Deliverables: <CheckCircle2 className="w-4 h-4" />,
  Tasks: <ListChecks className="w-4 h-4" />,
  'Gate Criteria': <Flag className="w-4 h-4" />,
};

export default function ImplementationPhase() {
  const { phaseId } = useParams<{ phaseId: string }>();
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState<Tab>('Description');

  const registry = getImplementationRegistry();
  const phase = getPhaseById(phaseId ?? '');

  if (!phase) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Phase not found</h2>
        <p className="text-gray-500 mb-6">The phase "{phaseId}" could not be loaded.</p>
        <Link to="/implementation" className="text-indigo-600 hover:underline text-sm flex items-center gap-1 justify-center">
          <ArrowLeft className="w-4 h-4" /> Back to Implementation Guide
        </Link>
      </div>
    );
  }

  const { meta } = phase;
  const statusColor = getPhaseStatusColor(meta.status);
  const statusLabel = getPhaseStatusLabel(meta.status, isAr);

  // Find previous and next phases
  const allPhases = registry.phases;
  const currentIdx = allPhases.findIndex((p) => p.meta.id === meta.id);
  const prevPhase = currentIdx > 0 ? allPhases[currentIdx - 1] : null;
  const nextPhase = currentIdx < allPhases.length - 1 ? allPhases[currentIdx + 1] : null;

  // Get workstream details for active workstreams
  const activeWorkstreams = meta.activeWorkstreams
    .map((id) => getWorkstreamById(id))
    .filter(Boolean);

  return (
    <div className={`max-w-5xl mx-auto px-4 py-8 ${isAr ? 'rtl' : 'ltr'}`} dir={isAr ? 'rtl' : 'ltr'}>

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <Link to="/implementation" className="hover:text-indigo-600 transition-colors">Implementation Guide</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-700 font-medium">Phase {meta.number}: {meta.titleEn}</span>
      </div>

      {/* Phase header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-6">
        {/* Color bar */}
        <div className="h-2 w-full" style={{ backgroundColor: meta.color }} />

        <div className="p-6">
          <div className="flex items-start gap-5">
            {/* Phase number */}
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0 shadow-sm"
              style={{ backgroundColor: meta.color }}
            >
              {meta.number}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  {isAr ? `المرحلة ${meta.number}` : `Phase ${meta.number}`}
                </span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor}`}>
                  {statusLabel}
                </span>
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {isAr ? meta.titleAr : meta.titleEn}
              </h1>
              <p className="text-gray-500 text-sm">
                {isAr ? meta.subtitleAr : meta.subtitleEn}
              </p>

              {/* Meta chips */}
              <div className="flex flex-wrap gap-3 mt-4">
                {meta.durationWeeks > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg">
                    <Clock className="w-4 h-4 text-gray-400" />
                    {meta.durationWeeks} {isAr ? 'أسابيع' : 'weeks'}
                  </div>
                )}
                {meta.startDate && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {meta.startDate}
                    {meta.endDate && ` → ${meta.endDate}`}
                  </div>
                )}
                {meta.dependsOn.length > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 bg-amber-50 px-3 py-1.5 rounded-lg">
                    <ChevronRight className="w-4 h-4 text-amber-500" />
                    Depends on: {meta.dependsOn.join(', ')}
                  </div>
                )}
              </div>

              {/* Progress bar */}
              {meta.progress > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>{isAr ? 'التقدم' : 'Progress'}</span>
                    <span className="font-semibold">{meta.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${meta.progress}%`, backgroundColor: meta.color }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active workstreams */}
        {activeWorkstreams.length > 0 && (
          <div className="border-t border-gray-100 px-6 py-4">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-2">
              {isAr ? 'مسارات العمل النشطة' : 'Active Workstreams'}
            </span>
            <div className="flex flex-wrap gap-2">
              {activeWorkstreams.map((ws) => ws && (
                <div
                  key={ws.id}
                  className="flex items-center gap-1.5 text-xs font-medium text-white px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: ws.color }}
                >
                  {ws.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Milestones strip */}
      {phase.milestones.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Milestone className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-700">{isAr ? 'المعالم' : 'Milestones'}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {phase.milestones.map((m, i) => (
              <div key={i} className="flex items-start gap-2 p-2.5 bg-gray-50 rounded-lg">
                <div
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                  style={{ backgroundColor: meta.color }}
                />
                <div>
                  <div className="text-xs font-medium text-gray-800 leading-snug">{m.title}</div>
                  {m.date && <div className="text-xs text-gray-400 mt-0.5">{m.date}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {TAB_ICONS[tab]}
            {tab}
            {tab === 'Objectives' && phase.objectives.length > 0 && (
              <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                {phase.objectives.length}
              </span>
            )}
            {tab === 'Tasks' && phase.tasks.length > 0 && (
              <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                {phase.tasks.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 min-h-64">

        {/* Description */}
        {activeTab === 'Description' && (
          <div>
            <h2 className="text-base font-bold text-gray-900 mb-4">
              {isAr ? 'لماذا هذه المرحلة؟' : 'Why This Phase Exists'}
            </h2>
            {phase.description ? (
              <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap">
                {phase.description}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No description available.</p>
            )}
          </div>
        )}

        {/* Objectives */}
        {activeTab === 'Objectives' && (
          <div>
            <h2 className="text-base font-bold text-gray-900 mb-4">
              {isAr ? 'الأهداف' : 'Objectives'} <span className="text-gray-400 font-normal text-sm">({phase.objectives.length})</span>
            </h2>
            {phase.objectives.length > 0 ? (
              <ul className="space-y-2.5">
                {phase.objectives.map((obj, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: meta.color }}
                    >
                      {i + 1}
                    </div>
                    <span className="text-sm text-gray-700 leading-relaxed">{obj}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-400 text-sm">No objectives listed.</p>
            )}
          </div>
        )}

        {/* Deliverables */}
        {activeTab === 'Deliverables' && (
          <div>
            <h2 className="text-base font-bold text-gray-900 mb-4">
              {isAr ? 'المخرجات الرئيسية' : 'Key Deliverables'} <span className="text-gray-400 font-normal text-sm">({phase.deliverables.length})</span>
            </h2>
            {phase.deliverables.length > 0 ? (
              <ul className="space-y-2">
                {phase.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700 leading-relaxed">{d}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-400 text-sm">No deliverables listed.</p>
            )}
          </div>
        )}

        {/* Tasks */}
        {activeTab === 'Tasks' && (
          <div>
            <h2 className="text-base font-bold text-gray-900 mb-4">
              {isAr ? 'المهام' : 'Tasks'} <span className="text-gray-400 font-normal text-sm">({phase.tasks.length})</span>
            </h2>
            {phase.tasks.length > 0 ? (
              <ul className="space-y-2">
                {phase.tasks.map((task, i) => (
                  <li key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                    <span
                      className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: meta.color + 'cc' }}
                    >
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700 leading-relaxed">{task}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-400 text-sm">No tasks listed.</p>
            )}
          </div>
        )}

        {/* Gate Criteria */}
        {activeTab === 'Gate Criteria' && (
          <div>
            <div className="flex items-start gap-3 mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Phase Gate</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  All criteria below must be met and signed off by the Steering Committee before the next phase begins.
                </p>
              </div>
            </div>
            <h2 className="text-base font-bold text-gray-900 mb-4">
              {isAr ? 'معايير بوابة المرحلة' : 'Gate Criteria'} <span className="text-gray-400 font-normal text-sm">({phase.gateCriteria.length})</span>
            </h2>
            {phase.gateCriteria.length > 0 ? (
              <ul className="space-y-2">
                {phase.gateCriteria.map((c, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
                    <Flag className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700 leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-400 text-sm">No gate criteria listed.</p>
            )}
          </div>
        )}
      </div>

      {/* Phase navigation */}
      <div className="flex items-center justify-between mt-6 gap-4">
        {prevPhase ? (
          <Link
            to={`/implementation/phase/${prevPhase.meta.id}`}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all text-sm font-medium text-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <div className="text-left">
              <div className="text-xs text-gray-400">Previous</div>
              <div>Phase {prevPhase.meta.number}: {prevPhase.meta.titleEn}</div>
            </div>
          </Link>
        ) : (
          <Link
            to="/implementation"
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-all text-sm font-medium text-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Implementation Guide
          </Link>
        )}

        {nextPhase ? (
          <Link
            to={`/implementation/phase/${nextPhase.meta.id}`}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all text-sm font-medium text-gray-700"
          >
            <div className="text-right">
              <div className="text-xs text-gray-400">Next</div>
              <div>Phase {nextPhase.meta.number}: {nextPhase.meta.titleEn}</div>
            </div>
            <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <Link
            to="/implementation/tracker"
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all text-sm font-medium"
          >
            Programme Tracker
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}
